import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Calendar, Check, CircleCheck, Clock, MapPin, MessageSquare, Mic } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { BOOKING_STEPS, serviceImage, serviceTint } from '@/lib/domain';
import { formatSlot } from '@/lib/format';
import { useUserId } from '@/components/AuthProvider';
import { useFeedback } from '@/components/Feedback';
import { Avatar, PageHeader, StatusBadge, Stars } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './BookingDetails.module.css';

type Proposal = {
  id: string;
  message: string | null;
  voice_url: string | null;
  price: number;
  status: string;
  provider_id: string;
  provider_name: string;
};

export default function BookingDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const userId = useUserId();
  const { toast, confirm } = useFeedback();

  const [booking, setBooking] = useState<any>(null);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data: bookingRow } = await supabase
        .from('bookings')
        .select(
          `id, status, address, description, preferred_date, preferred_time,
           final_price, completion_photo, rating, review_comment, services(name)`
        )
        .eq('id', id)
        .maybeSingle();
      setBooking(bookingRow);

      const { data: proposalRows } = await supabase
        .from('booking_proposals')
        .select(
          `id, message, voice_url, price, status, provider_id,
           profiles!booking_proposals_provider_id_fkey(full_name)`
        )
        .eq('booking_id', id)
        .order('created_at', { ascending: true });

      setProposals(
        (proposalRows ?? []).map((row: any) => ({
          id: row.id,
          message: row.message,
          voice_url: row.voice_url,
          price: row.price,
          status: row.status,
          provider_id: row.provider_id,
          provider_name: row.profiles?.full_name ?? 'Prestataire',
        }))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Notifications and the bookings list link to #propositions; the section only
  // exists once the data has loaded, so the browser cannot scroll to it by itself.
  useEffect(() => {
    if (loading || !hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, hash]);

  const acceptProposal = async (proposal: Proposal) => {
    const ok = await confirm({
      title: 'Accepter cette proposition ?',
      body: `${proposal.provider_name} réalisera la mission pour ${proposal.price} MAD.`,
      confirmLabel: 'Accepter',
    });
    if (!ok || !id || !userId) return;

    setAccepting(proposal.id);
    try {
      const { error: acceptError } = await supabase
        .from('booking_proposals')
        .update({ status: 'accepted' })
        .eq('id', proposal.id);
      if (acceptError) throw new Error(acceptError.message);

      const { error: rejectError } = await supabase
        .from('booking_proposals')
        .update({ status: 'rejected' })
        .eq('booking_id', id)
        .neq('id', proposal.id);
      if (rejectError) throw new Error(rejectError.message);

      const { error: bookingError } = await supabase
        .from('bookings')
        .update({
          status: 'assigned',
          provider_id: proposal.provider_id,
          final_price: proposal.price,
        })
        .eq('id', id);
      if (bookingError) throw new Error(bookingError.message);

      const { error: roomError } = await supabase.from('chat_rooms').upsert(
        {
          booking_id: id,
          client_id: userId,
          provider_id: proposal.provider_id,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'booking_id' }
      );
      if (roomError) throw new Error(roomError.message);

      toast('success', 'Proposition acceptée', 'Vous pouvez maintenant discuter avec le prestataire.');
      await load();
    } catch (err) {
      toast(
        'error',
        "Impossible d'accepter la proposition",
        err instanceof Error ? err.message : undefined
      );
    } finally {
      setAccepting(null);
    }
  };

  const openChat = async (proposal: Proposal) => {
    if (!id || !userId) return;
    let { data: room } = await supabase
      .from('chat_rooms')
      .select('id')
      .eq('booking_id', id)
      .maybeSingle();

    if (!room) {
      const { data: created } = await supabase
        .from('chat_rooms')
        .insert({ booking_id: id, client_id: userId, provider_id: proposal.provider_id })
        .select('id')
        .single();
      room = created;
    }

    if (room) navigate(`/app/messages/${room.id}`);
  };

  if (loading) return <Spinner block label="Chargement de la demande..." />;

  if (!booking) {
    return (
      <div>
        <PageHeader title="Détails de la demande" back="/app/bookings" />
        <p className={styles.notFound}>Demande introuvable.</p>
      </div>
    );
  }

  const stepIndex = BOOKING_STEPS.findIndex((step) => step.status === booking.status);
  const pendingProposals = proposals.filter((item) => item.status === 'pending');
  const acceptedProposal = proposals.find((item) => item.status === 'accepted');

  return (
    <div>
      <PageHeader title="Détails de la demande" back="/app/bookings" />

      <div className={styles.body}>
        <div className={styles.mainCol}>
          <section className={styles.serviceCard}>
            <span
              className={styles.serviceArt}
              style={{ background: serviceTint(booking.services?.name ?? '') }}
            >
              <img src={serviceImage(booking.services?.name ?? '')} alt="" />
            </span>
            <div className={styles.serviceText}>
              <div className={styles.serviceHead}>
                <h2 className={styles.serviceName}>{booking.services?.name ?? 'Service'}</h2>
                <StatusBadge status={booking.status} />
              </div>
              {booking.description ? (
                <p className={styles.serviceDescription}>{booking.description}</p>
              ) : null}
              {booking.final_price ? (
                <p className={`${styles.servicePrice} num`}>{booking.final_price} MAD</p>
              ) : null}
            </div>
          </section>

          <section className={styles.section}>
            <h2 className="section-title">Informations</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoRow}>
                <MapPin size={18} />
                <div>
                  <p className={styles.infoLabel}>Adresse</p>
                  <p className={styles.infoValue}>{booking.address}</p>
                </div>
              </div>
              {booking.preferred_date ? (
                <div className={styles.infoRow}>
                  <Calendar size={18} />
                  <div>
                    <p className={styles.infoLabel}>Date</p>
                    <p className={styles.infoValue}>{formatSlot(booking.preferred_date)}</p>
                  </div>
                </div>
              ) : null}
              {booking.preferred_time ? (
                <div className={styles.infoRow}>
                  <Clock size={18} />
                  <div>
                    <p className={styles.infoLabel}>Heure</p>
                    <p className={styles.infoValue}>{booking.preferred_time}</p>
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          {booking.status === 'completed' && booking.completion_photo ? (
            <section className={styles.section}>
              <h2 className="section-title">Photo du résultat</h2>
              <img
                className={styles.completionPhoto}
                src={booking.completion_photo}
                alt="Résultat de la mission"
              />
            </section>
          ) : null}

          {booking.status === 'completed' && booking.rating ? (
            <section className={styles.section}>
              <h2 className="section-title">Votre avis</h2>
              <div className={styles.ratingCard}>
                <Stars value={booking.rating} size={22} />
                {booking.review_comment ? (
                  <p className={styles.reviewComment}>{booking.review_comment}</p>
                ) : null}
              </div>
            </section>
          ) : null}
        </div>

        <div className={styles.sideCol}>
          <section className={styles.section}>
            <h2 className="section-title">Progression</h2>
            <ol className={styles.timeline}>
              {BOOKING_STEPS.map((step, index) => (
                <li
                  key={step.status}
                  className={`${styles.timelineItem} ${
                    index <= stepIndex ? styles.timelineItemOn : ''
                  } ${index === stepIndex ? styles.timelineItemCurrent : ''}`}
                  aria-current={index === stepIndex ? 'step' : undefined}
                >
                  <span className={styles.timelineDot}>
                    {index < stepIndex ? <Check size={14} strokeWidth={3} /> : null}
                  </span>
                  <span className={styles.timelineLabel}>{step.label}</span>
                </li>
              ))}
            </ol>
          </section>

          {acceptedProposal ? (
            <section className={styles.section}>
              <h2 className="section-title">Prestataire assigné</h2>
              <div className={styles.assignedCard}>
                <Avatar name={acceptedProposal.provider_name} size={46} tone="orange" />
                <div className={styles.assignedInfo}>
                  <p className={styles.providerName}>{acceptedProposal.provider_name}</p>
                  <p className={`${styles.price} num`}>{acceptedProposal.price} MAD</p>
                </div>
                <div className={styles.assignedActions}>
                  <Link
                    className="btn btn--navy btn--sm"
                    to={`/app/providers/${acceptedProposal.provider_id}`}
                  >
                    Profil
                  </Link>
                  {booking.status !== 'completed' ? (
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      onClick={() => void openChat(acceptedProposal)}
                    >
                      <MessageSquare size={14} /> Chat
                    </button>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}

          {booking.status === 'pending' ? (
            <section id="propositions" className={styles.section}>
              <h2 className="section-title">
                Propositions reçues ({pendingProposals.length})
              </h2>

              {pendingProposals.length === 0 ? (
                <div className={styles.waiting}>
                  <img src="/assets/illustrations/hourglass.png" alt="" />
                  <p>En attente de propositions des prestataires…</p>
                </div>
              ) : (
                <ul className={styles.proposalList}>
                  {pendingProposals.map((proposal) => (
                    <li key={proposal.id} className={styles.proposalCard}>
                      <div className={styles.proposalTop}>
                        <Avatar name={proposal.provider_name} size={44} tone="orange" />
                        <div className={styles.assignedInfo}>
                          <p className={styles.providerName}>{proposal.provider_name}</p>
                          <p className={`${styles.price} num`}>{proposal.price} MAD</p>
                        </div>
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          onClick={() => void acceptProposal(proposal)}
                          disabled={Boolean(accepting)}
                        >
                          {accepting === proposal.id ? (
                            <Spinner size={14} color="var(--ink)" />
                          ) : (
                            <>
                              <CircleCheck size={14} /> Accepter
                            </>
                          )}
                        </button>
                      </div>

                      {proposal.message ? (
                        <p className={styles.proposalMessage}>{proposal.message}</p>
                      ) : null}

                      {proposal.voice_url ? (
                        <div className={styles.voiceRow}>
                          <Mic size={15} color="var(--orange)" />
                          <audio className={styles.audio} controls src={proposal.voice_url} />
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
